import { DISCIPLINES, DISC_TO_JOB } from "./constants";
import { FIRST_NAMES, LAST_NAMES } from "./records";
import { fnv1a, makeRng } from "./seed";
import type { Service } from "./types";

export interface ClinicianRow {
  id: string;
  name: string;
  disc: string;
  job: string;
  ftpt: string;
  branch: string;
  reg: number;
  ot: number;
  ato: number;
  sick: number;
  nonVisit: number;
  capped: number;
  attain: number;
  actual: number;
  vmin: number;
  nonOpt: number;
}

export interface RosterFilters {
  branch: string[];
  discipline: string[];
  jobTitle: string[];
  ftpt: string;
  attain: [number, number];
  vmin: [number, number];
}

/** Clinicians are still seeded, but they are distributed across the LIVE branch
 *  list from `dashboard/config` so the roster lines up with the branch filter.
 *  Returns nothing until the branch list has loaded. */
export function buildRoster(service: Service, branches: string[]): ClinicianRow[] {
  if (!branches.length) return [];
  const rows: ClinicianRow[] = [];
  for (let i = 0; i < 34; i++) {
    const rng = makeRng(fnv1a(service + "|clin|" + i));
    const ri = (a: number, b: number) => a + Math.floor(rng() * (b - a + 1));
    const disc = DISCIPLINES[ri(0, DISCIPLINES.length - 1)];
    const name = FIRST_NAMES[ri(0, FIRST_NAMES.length - 1)] + " " + LAST_NAMES[ri(0, LAST_NAMES.length - 1)];
    const ftpt = rng() < 0.78 ? "Full Time" : "Part Time";
    const branch = branches[ri(0, branches.length - 1)];
    const capped = 24 + rng() * 6;
    const attain = 88 + rng() * 16;
    rows.push({
      id: "C" + i, name, disc, job: DISC_TO_JOB[disc], ftpt, branch,
      reg: ftpt === "Full Time" ? 36 + rng() * 6 : 18 + rng() * 8,
      ot: rng() * 5, ato: rng() * 8, sick: rng() * 4, nonVisit: rng() * 6,
      capped, attain, actual: capped * attain / 100, vmin: 42 + rng() * 16, nonOpt: 4 + rng() * 12,
    });
  }
  return rows;
}

export function filterRoster(roster: ClinicianRow[], f: RosterFilters): ClinicianRow[] {
  return roster.filter((r) => {
    if (f.branch.length && !f.branch.includes(r.branch)) return false;
    if (f.discipline.length && !f.discipline.includes(r.disc)) return false;
    if (f.jobTitle.length && !f.jobTitle.includes(r.job)) return false;
    if (f.ftpt !== "All" && r.ftpt !== f.ftpt) return false;
    if (r.attain < f.attain[0] || r.attain > f.attain[1]) return false;
    if (r.vmin < f.vmin[0] || r.vmin > f.vmin[1]) return false;
    return true;
  });
}

export interface ClinCol {
  key: string;
  label: string;
  get: (r: ClinicianRow) => string | number;
  who?: boolean;
  tag?: boolean;
  num?: boolean;
  fmt?: (v: number) => string;
  state?: (r: ClinicianRow) => string;
}
export const CLIN_COLS: ClinCol[] = [
  { key: "name", label: "Clinician", get: (r) => r.name, who: true },
  { key: "disc", label: "Disc", get: (r) => r.disc, tag: true },
  { key: "job", label: "Job Title", get: (r) => r.job },
  { key: "ftpt", label: "FT/PT", get: (r) => r.ftpt },
  { key: "reg", label: "Reg Hrs", get: (r) => r.reg, num: true, fmt: (v) => v.toFixed(1) },
  { key: "ot", label: "OT", get: (r) => r.ot, num: true, fmt: (v) => v.toFixed(1) },
  { key: "actual", label: "Actual Pts", get: (r) => r.actual, num: true, fmt: (v) => v.toFixed(1) },
  { key: "capped", label: "Capped Pts", get: (r) => r.capped, num: true, fmt: (v) => v.toFixed(1) },
  { key: "attain", label: "Attainment", get: (r) => r.attain, num: true, fmt: (v) => v.toFixed(1) + "%", state: (r) => r.attain >= 98 ? "good" : "bad" },
  { key: "vmin", label: "Visit Min", get: (r) => r.vmin, num: true, fmt: (v) => Math.round(v) + "m" },
  { key: "nonOpt", label: "Non-Opt %", get: (r) => r.nonOpt, num: true, fmt: (v) => v.toFixed(1) + "%" },
];
